/**
 * Incoming Call Modal
 * Shows notification when receiving a video call
 */
import React, { useEffect, useRef } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Phone, Video, X } from 'lucide-react';
import { useVideoCall } from '../../context/VideoCallContext';

const IncomingCallModal = () => {
  const { incomingCall, answerCall, declineCall } = useVideoCall();

  // Ringtone — internal video/voice calls were SILENT before iter144.
  // The PSTN side already plays a ringtone via TwilioVoiceContext; the
  // internal (user-to-user) side just rendered this modal with no
  // audio cue, so anyone away from their screen never knew a call had
  // arrived. We reuse the same WAV the PSTN side uses for parity.
  // Audio plays on a loop; stops automatically when the modal
  // unmounts (call answered, declined, cancelled by caller, or timed
  // out — all of which clear `incomingCall`).
  const ringtoneRef = useRef(null);
  useEffect(() => {
    if (!incomingCall) return undefined;
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    const audio = new Audio(`${apiBase}/api/media/calling/ringtone_incoming.wav`);
    audio.loop = true;
    audio.volume = 0.6;
    ringtoneRef.current = audio;
    // Browser autoplay policy: play() can fail if the user hasn't
    // interacted with the page yet. The login click already counts as
    // a gesture for this origin, but we still .catch so a blocked
    // play doesn't crash anything — the visual modal still appears
    // and the user can tap to answer.
    try { audio.play().catch(() => {}); } catch (e) { /* ignore */ }
    return () => {
      try { audio.pause(); audio.currentTime = 0; audio.src = ''; } catch (e) { /* ignore */ }
      ringtoneRef.current = null;
    };
  }, [incomingCall]);

  if (!incomingCall) return null;

  const caller = incomingCall.caller || {};
  const isVideoCall = incomingCall.callMode === 'video';

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleGradient = (role) => {
    switch (role) {
      case 'admin': return 'from-rose-500 to-pink-600';
      case 'supervisor': return 'from-blue-500 to-indigo-600';
      case 'lead': return 'from-emerald-500 to-teal-600';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent className="max-w-sm w-[calc(100%-2rem)] p-0 overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-3xl left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className="p-6 text-center">
          {/* Animated ring effect */}
          <div className="relative w-24 h-24 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full bg-green-500/20 animate-ping" />
            <div className="absolute inset-2 rounded-full bg-green-500/30 animate-ping animation-delay-100" />
            <Avatar className="relative w-24 h-24 border-4 border-green-500">
              <AvatarImage src={caller.profile_picture} />
              <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(caller.role)} text-white text-2xl`}>
                {getInitials(caller.name)}
              </AvatarFallback>
            </Avatar>
          </div>

          <h2 className="text-xl font-bold mb-1">{caller.name || 'Unknown'}</h2>
          <p className="text-sm text-muted-foreground mb-1 capitalize">{caller.role}</p>
          <p className="text-sm text-green-600 mb-6">
            Incoming {isVideoCall ? 'Video' : 'Voice'} Call...
          </p>

          {/* Call actions */}
          <div className="flex justify-center gap-8">
            <Button
              size="icon"
              variant="destructive"
              className="h-16 w-16 rounded-full"
              onClick={() => declineCall()}
              data-testid="decline-call-btn"
            >
              <X className="w-6 h-6" />
            </Button>
            
            <Button
              size="icon"
              className="h-16 w-16 rounded-full bg-green-500 hover:bg-green-600"
              onClick={() => answerCall()}
              data-testid="answer-call-btn"
            >
              {isVideoCall ? (
                <Video className="w-6 h-6" />
              ) : (
                <Phone className="w-6 h-6" />
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IncomingCallModal;
